import { createStructuredEnvelope, isStructuredEnvelope, readStructuredEnvelope, type StructuredDocumentEnvelope } from '@adornis/web-editor/migrations/structured.js';
import type { StructuredValue } from '@adornis/web-editor/nodes/structured-document.js';
import type { WebsiteBuilderDocumentEnvelope } from '@adornis/web-editor/types.js';
import type { LandingEditorProject } from './landingEditorTypes.js';
import { landingStructureMapping } from './landingStructureMapping.js';

export const LANDING_EDITOR_WEBSITE_PROJECT_ID = 'viva-porta-website';
export const LANDING_EDITOR_WEBSITE_FORMAT = 'adornis.web-editor';
export type LandingEditorWebsiteDocument = StructuredDocumentEnvelope | WebsiteBuilderDocumentEnvelope;

export const createLandingEditorWebsiteDocument = (project: LandingEditorProject, revision?: string): StructuredDocumentEnvelope =>
  createStructuredEnvelope(LANDING_EDITOR_WEBSITE_PROJECT_ID, JSON.parse(JSON.stringify(project)) as StructuredValue, project.updatedAt, revision, landingStructureMapping);

export const isLandingEditorWebsiteDocument = (value: unknown): value is LandingEditorWebsiteDocument => {
  if (!value || typeof value !== 'object') return false;
  if (isStructuredEnvelope(value)) return value.projectId === LANDING_EDITOR_WEBSITE_PROJECT_ID;
  const document = value as Partial<WebsiteBuilderDocumentEnvelope>;
  return document.format === 'adornis.website' && document.version === 1 && document.projectId === LANDING_EDITOR_WEBSITE_PROJECT_ID && Boolean(document.doc);
};

export const landingEditorProjectInput = (value: unknown): unknown => {
  if (!isLandingEditorWebsiteDocument(value)) return value;
  if (isStructuredEnvelope(value)) return readStructuredEnvelope(value);
  const site = value.doc.content?.find(node => node.attrs?.componentType === 'viva-site');
  const props = site?.attrs?.props;
  return props && typeof props === 'object' ? (props as Record<string, unknown>).project : null;
};

export const landingEditorDocumentRevision = (value: unknown) =>
  isLandingEditorWebsiteDocument(value) && typeof value.revision === 'string' ? value.revision : undefined;

export const nextLandingEditorDocumentRevision = (...values: unknown[]) => {
  const current = values.reduce<number>((maximum, value) => {
    const revision = Number(landingEditorDocumentRevision(value));
    return Number.isFinite(revision) ? Math.max(maximum, revision) : maximum;
  }, 0);
  return String(current + 1);
};

export const matchesLandingEditorDocumentRevision = (current: unknown, incoming: unknown) => {
  const currentRevision = landingEditorDocumentRevision(current);
  return currentRevision === undefined || landingEditorDocumentRevision(incoming) === currentRevision;
};
